import React, { useState, useEffect, useRef } from "react";
import SoftTypography from "StyleFeatures/SoftTypography";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import { Space } from "antd";
import "jspdf-autotable";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { ExpenseManagemnetStatusLabel } from "Constants/Enums/expenseManagmentEnum";

function ExpenseTable() {
  const [expenseData, setExpenseData] = useState([]);
  const BASE_URL = process.env.REACT_APP_Base_URL;
  const didFetchRef = useRef(false);

  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    const fetchExpenseData = async () => {
      try {
        const response = await axiosInstance.get(API.expenseManagement.getEmployeeExpenses);
        const expenses = Array.isArray(response.data) ? response.data : [];
        setExpenseData(expenses);
      } catch (error) {
        console.error("Error fetching expense data:", error);
        setExpenseData([]);
      }
    };

    fetchExpenseData();
  }, []);

  const handleDownload = (expense) => {
     const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Expense Report", 14, 22);

    // Line styling
    const lineY = 28;
    doc.setLineWidth(0.5);
    doc.setDrawColor(192, 192, 192);
    doc.line(14, lineY, 200, lineY);

    const marginTop = 4;

    // Use approver instead of manager
    const approverName = expense.approver
      ? `${expense.approver.firstName || ""} ${expense.approver.lastName || ""}`
      : "N/A";

    const departmentName = expense.department ? `${expense.department.name || ""}` : "N/A";

    const formattedDate = expense.date ? new Date(expense.date).toLocaleDateString() : "N/A";

    const statusLabel = ExpenseManagemnetStatusLabel[expense.status] || "Unknown";

    doc.setFontSize(12);
    doc.text(`Approver: ${approverName}`, 14, 34 + marginTop);
    doc.text(`Requested Date: ${formattedDate}`, 14, 44 + marginTop);
    doc.text(`Description: ${expense.description || "N/A"}`, 14, 54 + marginTop);
    doc.text(`Department: ${departmentName}`, 14, 64 + marginTop);
    doc.text(`Charges: ${expense.charges || 0}`, 14, 74 + marginTop);
    doc.text(`Status: ${statusLabel}`, 14, 84 + marginTop);

    doc.save(`expense_report_${expense.id || ""}.pdf`);
  };

  const handlePdfPreview = (filePath) => {
    if (!filePath) {
      console.error("File path not found");
      return;
    }

    // If it's already a full URL (e.g. starts with http or https)
    const isFullUrl = /^https?:\/\//i.test(filePath);

    // Build the full URL properly
    const fullUrl = isFullUrl
      ? filePath
      : `${BASE_URL}${filePath.startsWith("/") ? "" : "/"}${filePath}`;

    // Open file in new tab
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const rows = expenseData.map((expense, index) => ({
    RequestedDate: (
      <SoftTypography
        key={`date_${index}`}
        variant="body2"
        color="dark"
        fontWeight="medium"
      >
        {expense.date ? new Date(expense.date).toLocaleDateString() : "N/A"}
      </SoftTypography>
    ),
    Manager: (
      <SoftTypography
        key={`manager_${index}`}
        variant="body2"
        color="dark"
        fontWeight="medium"
      >
        {expense.approver ? `${expense.approver.firstName} ${expense.approver.lastName}` : "N/A"}
      </SoftTypography>
    ),
    Department: (
      <SoftTypography
        key={`department_${index}`}
        variant="body2"
        color="dark"
        fontWeight="medium"
      >
        {expense.department?.name}
      </SoftTypography>
    ),
    Charges: (
      <SoftTypography
        key={`charges_${index}`}
        variant="body2"
        color="dark"
        fontWeight="medium"
      >
        {expense.charges ? `USD ${expense.charges.toLocaleString()}` : "USD 0"}
      </SoftTypography>
    ),
    Description: (
      <SoftTypography
        key={`type_${index}`}
        variant="body2"
        color="dark"
        fontWeight="medium"
      >
        {expense.description || "N/A"}
      </SoftTypography>
    ),
    Receipt: (
      <Space key={`receipt_${index}`}>
        {expense.receipt ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handlePdfPreview(expense.receipt);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "#599c16",
              fontWeight: 500,
              padding: "4px 8px",
              borderRadius: "4px",
              transition: "background-color 0.3s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f7fee7")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <EyeOutlined style={{ fontSize: 16, marginRight: 6 }} />
            <span style={{ fontSize: "14px" }}>View</span>
          </div>
        ) : (
          <span style={{ color: "#999", fontSize: "14px" }}>—</span>
        )}
      </Space>
    ),
    CalculationSheet: (
      <Space key={`calculationSheet_${index}`}>
        {expense.calculationSheet ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handlePdfPreview(expense.calculationSheet);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "#52c41a",
              fontWeight: 500,
              padding: "4px 8px",
              borderRadius: "4px",
              transition: "background-color 0.3s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f6ffed")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <EyeOutlined style={{ fontSize: 16, marginRight: 6 }} />
            <span style={{ fontSize: "14px" }}>View</span>
          </div>
        ) : (
          <span style={{ color: "#999", fontSize: "14px" }}>—</span>
        )}
      </Space>
    ),
    Status: (
      <SoftTypography
        key={`status_${index}`}
        variant="body2"
        fontWeight="medium"
        style={{
          padding: "4px 12px",
          borderRadius: "16px",
          display: "inline-block",
          fontSize: "13px",
        }}
        color={
          expense.status === "APPROVED"
            ? "success"
            : expense.status === "REJECTED"
            ? "error"
            : "info"
        }
      >
        {ExpenseManagemnetStatusLabel[expense.status]}
      </SoftTypography>
    ),
    Download: (
      <DownloadOutlined
        key={`download_${index}`}
        className="expense-download-icon"
        onClick={() => handleDownload(expense)}
        style={{ fontSize: 18, cursor: "pointer", color: "#599c16" }}
      />
    ),
  }));

  const columns = [
    { title: "Requested Date", name: "RequestedDate", align: "center" },
    { title: "Description", name: "Description", align: "center" },
    { title: "Department", name: "Department", align: "center" },
    { title: "Charges", name: "Charges", align: "center" },
    { title: "Receipt", name: "Receipt", align: "center" },
    { title: "Expense Sheet", name: "CalculationSheet", align: "center" },
    { title: "Status", name: "Status", align: "center" },
    { title: "Download", name: "Download", align: "center" },
  ];

  return { columns, rows };
}

export default ExpenseTable;